import PropTypes from 'prop-types';
import React, { Suspense } from 'react';
import { Transition as ReactTransition } from 'react-transition-group';
import Browser from '@wetransfer/concorde-browser';

import PanelErrorBoundary from './panel-error-boundary';
import PanelTopBar from './panel-top-bar';

import Transition from './transition';

import Router from '@module/router';
import I18n from '@module/i18n';

import { shouldAskBeforeLeaving } from '@app/helper/route.js';

import Store from '@app/store';
import Actions from '@app/action';

import Footer from '@component/footer';
import Scrollable from '@component/scrollable';
import Button from '@component/form/element/button';
import NotificationBar from '@component/info/notificationbar';
import ActionBar from '@component/info/actionbar';
import AccountWarning from '@component/info/accountwarning';

export default class Panel extends React.Component {
  static propTypes = {
    appear: PropTypes.bool,
    in: PropTypes.bool,
    locale: PropTypes.string,
    route: PropTypes.object,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    // We'll store the current `route.path` and the corresponding `content`
    // inside the component state.
    this.state = {
      returnPath: null,
      returnName: null,
    };

    this.closePanel = this.closePanel.bind(this);
    this.leavePage = this.leavePage.bind(this);
    this.setReturnPath = this.setReturnPath.bind(this);
    this.stayOnPage = this.stayOnPage.bind(this);
    this.onEnter = this.onEnter.bind(this);
    this.onExit = this.onExit.bind(this);
  }

  // When the component appears
  onExit(component) {
    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }

    this.currentTransition = new Transition({
      prevType: false,
      newType: this.props.route.type,
      component: component,
      animate: false,
    });
  }

  // When component appears animate
  onEnter(component) {
    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }
    this.currentTransition = new Transition({
      prevType: false,
      newType: this.props.route.type,
      component: component,
      animate: !this.props.appear,
    });
  }

  // When component is updated
  componentDidUpdate(prevProps) {
    // This will stop updates from happening if the panel is leaving
    // and the current route is transfer (so -> no panel)
    const { route } = Store.getState();

    if (this.currentTransition) {
      this.currentTransition.stop(true);
    }

    if (this.node) {
      this.currentTransition = new Transition({
        prevType: prevProps.route.type,
        newType: route.type,
        component: this.node,
      });
    }

    if (prevProps.route.path !== this.props.route.path) {
      Store.dispatch(Actions.Notification.resetNotification());
      if (this.scrollableNode) {
        this.scrollableNode.scrollTo('top');
      }
    }
  }

  // Set the returnPath for a panel
  setReturnPath(path, name) {
    this.setState({ returnPath: path, returnName: name });
  }

  componentWillUnmount() {
    clearTimeout(this.scrollTimer);
    Store.dispatch(Actions.Route.setFormState(false));
  }

  closePanel() {
    Router.navigateTo(Router.getLastRoute('transfer', '/'));
  }

  resetReturnPath = () => {
    this.setReturnPath(null, null);
  };

  stayOnPage() {
    this.hideActionBar(() => {
      Store.dispatch(Actions.Route.setNextAction(null));
    });
  }

  leavePage() {
    const { route } = this.props;

    if (route.isMobileTransfer) {
      // create a new transfer to delete the current one
      Store.dispatch(Actions.Transfer.create());
    }

    this.hideActionBar(() => {
      Store.dispatch(Actions.Route.setFormState(false));
      route.nextRoute();
    });
  }

  hideActionBar(callback) {
    if (this.actionBarNode) {
      this.actionBarNode.hide(callback);
    }
  }

  renderNotificationBar(notification) {
    if (notification.panel.html === undefined) {
      return null;
    }

    return <NotificationBar key="panel_notification_bar" />;
  }

  makeInactiveAccountWarning(account) {
    const expired = account.subscription.expires_at;
    const titleKey = expired
      ? 'panel.warning.account_expired_title'
      : 'panel.warning.account_inactive_title';
    const subtitleKey = expired
      ? 'panel.warning.account_expired_subtitle'
      : 'panel.warning.account_inactive_subtitle';
    const actionTextKey = expired
      ? 'panel.warning.account_expired_action'
      : 'panel.warning.account_inactive_action';
    const action = () => Router.navigateTo('/payment/retry', false);
    return (
      <AccountWarning
        key="panel_account_warning"
        title={I18n.t(titleKey)}
        subtitle={I18n.t(subtitleKey)}
        actionText={I18n.t(actionTextKey)}
        action={action}
      />
    );
  }

  render() {
    const { account, route, user, notification } = Store.getState();
    const contents = [];
    const inActiveWarning =
      !account.active_subscription && route.auth && !route.payment;
    const isUserLoggedIn = Boolean(user.loggedIn);
    if (inActiveWarning && isUserLoggedIn) {
      contents.push(this.makeInactiveAccountWarning(account));
    }

    const props = {
      locale: this.props.locale,
      route: this.props.route,
      page: this,
      key: 'panel_route_component',
    };
    props.setReturnPath = this.setReturnPath;

    /*
     * These are components declared in navigation/config.js
     * By default, each component created will have
     * locale, route, page, setReturnPath as props
     */
    const Component = this.props.route.component;
    contents.push(
      <Suspense key="panel_content" fallback={null}>
        <PanelErrorBoundary>
          <Component {...props} />
        </PanelErrorBoundary>
      </Suspense>
    );

    // content
    const panelContentClassName = ['panel__content'];
    if (inActiveWarning) {
      panelContentClassName.push('panel__content--inactive-content');
    }

    // Add route name to the panel for custom behavior/styling
    if (this.props.route.name) {
      panelContentClassName.push(this.props.route.name);
    }

    const action =
      route.nextRoute && shouldAskBeforeLeaving(route) ? (
        <ActionBar
          ref={(node) => (this.actionBarNode = node)}
          text={I18n.t('form.confirm_leave')}
          key="panel_action"
        >
          <Button
            type="actionbar-action"
            text={I18n.t('form.stay_on_page')}
            onClick={this.stayOnPage}
          />
          <Button
            type="actionbar-cancel"
            text={I18n.t('form.leave')}
            onClick={this.leavePage}
          />
        </ActionBar>
      ) : null;
    let content = [
      <div key="panel_content" className={panelContentClassName.join(' ')}>
        {contents}
      </div>,
      this.renderNotificationBar(notification),
      action,
    ];

    if (!Browser.isMobile) {
      content.push(<Footer key="panel_footer" />);
    }

    if (!Browser.isMobile) {
      content = (
        <Scrollable
          className="panel__scrollable"
          ref={(node) => (this.scrollableNode = node)}
        >
          {content}
        </Scrollable>
      );
    }

    return (
      <ReactTransition
        appear={this.props.appear}
        in={this.props.in}
        timeout={{ in: 0, exit: 1000 }}
        onEnter={this.onEnter}
        onEntered={this.onEntered}
        onExit={this.onExit}
        unmountOnExit={true}
      >
        <div
          className="panel panel--no-transition"
          ref={(node) => (this.node = node)}
        >
          {!Browser.isMobile && (
            <PanelTopBar
              returnName={this.state.returnName}
              returnPath={this.state.returnPath}
              onCloseClick={this.closePanel}
              onBackClick={this.resetReturnPath}
            />
          )}
          {content}
        </div>
      </ReactTransition>
    );
  }
}
